import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Search, User, CreditCard, Users, MessageCircle, Bell, ShieldCheck, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user?.id]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF6F0]/90 border-b border-[#EAE3D2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img
            data-testid="nav-brand"
            src="/logo-emblem.png"
            alt="EliteNRIMatrimony"
            className="h-14 sm:h-16 lg:h-20 w-auto object-contain"
          />
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#6B1D2F] leading-none text-center">
              Elite<span className="text-[#B8860B]">NRI</span>Matrimony
            </span>
            <span
              data-testid="nav-tagline"
              className="-mt-0.5 flex items-center gap-2 font-serif italic text-xs sm:text-sm text-slate-900 tracking-wide whitespace-nowrap"
            >
              <span aria-hidden="true" className="h-px flex-1 bg-[#B8860B]/60" />
              Bridging hearts Beyond Borders
              <span aria-hidden="true" className="h-px flex-1 bg-[#B8860B]/60" />
            </span>
          </div>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          {user ? (
            <>
              <Link to="/search" data-testid="nav-search"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><Search className="w-4 h-4 mr-1" />Search</Button></Link>
              <Link to="/matches" data-testid="nav-matches"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><Users className="w-4 h-4 mr-1" />My Matches</Button></Link>
              <Link to="/chat" data-testid="nav-chat"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><MessageCircle className="w-4 h-4 mr-1" />Chat</Button></Link>
              {user?.role === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="nav-admin" variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><ShieldCheck className="w-4 h-4 mr-1" />Admin<ChevronDown className="w-3 h-3 ml-1" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild><Link to="/admin/members" data-testid="nav-admin-members">Members</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/verifications" data-testid="nav-admin-verifications">Verifications</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/reports" data-testid="nav-admin-reports">Reports</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/audit" data-testid="nav-admin-audit">Audit log</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/usage" data-testid="nav-admin-usage">Usage & costs</Link></DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Link to="/pricing" data-testid="nav-pricing"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><CreditCard className="w-4 h-4 mr-1" />Plans</Button></Link>
              <Link to="/notifications" data-testid="nav-notifications" className="relative">
                <Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F] px-2"><Bell className="w-4 h-4" /></Button>
                {unread > 0 && <span data-testid="nav-unread-badge" className="absolute top-0 right-0 bg-[#6B1D2F] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{unread}</span>}
              </Link>
              <Link to="/dashboard" data-testid="nav-dashboard"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]"><User className="w-4 h-4 mr-1" />{user.full_name?.split(" ")[0] || "Me"}</Button></Link>
              <Button data-testid="nav-logout" variant="outline" onClick={() => { logout(); nav("/"); }} className="border-[#6B1D2F]/30 text-[#6B1D2F]"><LogOut className="w-4 h-4 mr-1" />Sign out</Button>
            </>
          ) : (
            <>
              <Link to="/pricing" data-testid="nav-pricing"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]">Plans</Button></Link>
              <Link to="/login" data-testid="nav-login"><Button variant="ghost" className="text-slate-700 hover:text-[#6B1D2F]">Sign in</Button></Link>
              <Link to="/register" data-testid="nav-register"><Button className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-5">Join Free</Button></Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
